import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Clock, ArrowRight } from 'lucide-react'
import { CHAPTERS, chapterText } from '@/lib/chapters'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { getChapterCompletion } from '@/components/SlideWizard'
import ResetCourseProgress from '@/components/ResetCourseProgress'
import { useEffect, useState } from 'react'

const ease = [0.16, 1, 0.3, 1] as const

export const Route = createFileRoute('/basics/')({
  component: BasicsIndex,
})

function BasicsIndex() {
  const [completionMap, setCompletionMap] = useState<Record<string, number>>({})
  const t = useUi()
  const locale = useLocale()

  useEffect(() => {
    const map: Record<string, number> = {}
    CHAPTERS.forEach(c => {
      const total = c.chapter.slides.length
      const { completedCount } = getChapterCompletion(c.chapter.id)
      map[c.chapter.id] = total > 0 ? Math.round((completedCount / total) * 100) : 0
    })
    setCompletionMap(map)
  }, [])

  return (
    <div className="mx-auto max-w-5xl px-6 pb-20 pt-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
      >
        <p className="text-sm font-semibold text-violet-600">
          {t.basicsEyebrow}
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
          {t.basicsTitle}
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-slate-500">
          {t.basicsIntro}
        </p>
        <div className="mt-5">
          <ResetCourseProgress
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            onReset={() => setCompletionMap({})}
          />
        </div>
      </motion.div>

      <ul className="mt-12 space-y-4">
        {CHAPTERS.map((c, i) => {
          const percent = completionMap[c.chapter.id] ?? 0
          const text = chapterText(c, locale)
          return (
            <motion.li
              key={c.basicsSlug}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.06, ease }}
            >
              <Link
                to="/basics/$basicsSlug"
                params={{ basicsSlug: c.basicsSlug }}
                className="group block rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md"
              >
                <div className="flex items-start gap-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-base font-semibold text-violet-700 tabular-nums">
                    {c.number}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold tracking-tight text-slate-900 group-hover:text-violet-700 sm:text-2xl">
                      {text.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-500 sm:text-base">
                      {text.description}
                    </p>
                    <div className="mt-4 flex items-center gap-4 text-sm text-slate-400">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {t.minutes(c.chapter.estimatedMinutes)}
                      </span>
                      {percent > 0 && (
                        <span className="text-violet-600">{t.percentComplete(percent)}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="mt-2 h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-500" />
                </div>
              </Link>
            </motion.li>
          )
        })}
      </ul>
    </div>
  )
}
